import React, { useState, Fragment } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Person,
  Email,
  Phone,
  VpnKey,
} from '@mui/icons-material';
import { repAPI } from '../../services/api';
import LeadLoginRequestDialog from './LeadLoginRequestDialog';

const CONTACT_ROLES = ['VET', 'VET_TECH', 'BILLING', 'ADMIN', 'OTHER'];

const LeadContacts = ({ lead, onContactUpdated }) => {
  const contacts = lead?.contacts || [];
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [loginRequestDialogOpen, setLoginRequestDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [contactForLogin, setContactForLogin] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    prefix: '',
    firstName: '',
    lastName: '',
    suffix: '',
    email: '',
    phone: '',
    role: 'OTHER',
    title: '',
    isPrimary: false,
    supervisingVetContactId: '',
  });

  const getRoleColor = (role) => {
    switch (role) {
      case 'VET': return 'primary';
      case 'VET_TECH': return 'secondary';
      case 'BILLING': return 'warning';
      case 'ADMIN': return 'info';
      default: return 'default';
    }
  };

  const formatRole = (role) => {
    return role
      .split('_')
      .map(word => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ');
  };

  const handleOpenDialog = (contact = null) => {
    if (contact) {
      setEditingContact(contact);
      setFormData({
        prefix: contact.prefix || '',
        firstName: contact.firstName || '',
        lastName: contact.lastName || '',
        suffix: contact.suffix || '',
        email: contact.email || '',
        phone: contact.phone || '',
        role: contact.role || 'OTHER',
        title: contact.title || '',
        isPrimary: contact.isPrimary || false,
        supervisingVetContactId: contact.supervisingVetContactId || '',
      });
    } else {
      setEditingContact(null);
      setFormData({
        prefix: '',
        firstName: '',
        lastName: '',
        suffix: '',
        email: '',
        phone: '',
        role: 'OTHER',
        title: '',
        isPrimary: false,
        supervisingVetContactId: '',
      });
    }
    setDialogOpen(true);
    setError('');
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingContact(null);
    setFormData({
      prefix: '',
      firstName: '',
      lastName: '',
      suffix: '',
      email: '',
      phone: '',
      role: 'OTHER',
      title: '',
      isPrimary: false,
      supervisingVetContactId: '',
    });
    setError('');
  };

  const handleSubmit = async () => {
    // Validate required fields
    if (!formData.firstName || !formData.firstName.trim()) {
      setError('First name is required');
      return;
    }
    if (!formData.lastName || !formData.lastName.trim()) {
      setError('Last name is required');
      return;
    }
    if (!formData.email || !formData.email.trim()) {
      setError('Email is required');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Invalid email format');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const contactData = {
        prefix: formData.prefix?.trim() || null,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        suffix: formData.suffix?.trim() || null,
        email: formData.email.trim(),
        phone: formData.phone?.trim() || null,
        role: formData.role,
        title: formData.title?.trim() || null,
        isPrimary: formData.isPrimary,
        supervisingVetContactId: formData.supervisingVetContactId || null,
      };

      if (editingContact) {
        await repAPI.updateLeadContact(lead.id, editingContact.id, contactData);
      } else {
        await repAPI.addLeadContact(lead.id, contactData);
      }

      handleCloseDialog();
      onContactUpdated();
    } catch (err) {
      setError(err.message || (editingContact ? 'Failed to update contact' : 'Failed to add contact'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!contactToDelete) return;

    try {
      setLoading(true);
      setError('');
      
      await repAPI.deleteLeadContact(lead.id, contactToDelete.id);
      
      setDeleteConfirmOpen(false);
      setContactToDelete(null);
      onContactUpdated();
    } catch (err) {
      setError(err.message || 'Failed to delete contact');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: 'Poppins, sans-serif' }}>
            Contacts
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            Add Contact
          </Button>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {contacts.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Poppins, sans-serif' }}>
              No contacts yet. Add contacts to track multiple people at this organization.
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {contacts.map((contact, index) => (
              <Fragment key={contact.id}>
                <ListItem
                  secondaryAction={
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => handleOpenDialog(contact)}
                        title="Edit contact"
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => {
                          setContactForLogin(contact);
                          setLoginRequestDialogOpen(true);
                          setError('');
                        }}
                        title="Request login"
                        sx={{ color: 'primary.main' }}
                      >
                        <VpnKey fontSize="small" />
                      </IconButton>
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => {
                          setContactToDelete(contact);
                          setDeleteConfirmOpen(true);
                          setError('');
                        }}
                        title="Delete contact"
                        sx={{ color: 'error.main' }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="subtitle2" sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600 }}>
                          {[contact.prefix, contact.firstName, contact.lastName, contact.suffix].filter(Boolean).join(' ')}
                          {contact.isPrimary && (
                            <Chip label="Primary" size="small" color="primary" sx={{ ml: 1 }} />
                          )}
                        </Typography>
                        <Chip 
                          label={formatRole(contact.role)} 
                          size="small" 
                          color={getRoleColor(contact.role)}
                        />
                      </Box>
                    }
                    secondary={
                      <Box>
                        {contact.title && (
                          <Typography variant="body2" sx={{ fontFamily: 'Poppins, sans-serif', mb: 0.5 }}>
                            {contact.title}
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Email fontSize="small" sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="body2" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                              {contact.email}
                            </Typography>
                          </Box>
                          {contact.phone && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Phone fontSize="small" sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="body2" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                                {contact.phone}
                              </Typography>
                            </Box>
                          )}
                          {contact.supervisingVet && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                              <Person fontSize="small" sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="body2" sx={{ fontFamily: 'Poppins, sans-serif' }}>
                                Supervised by: {contact.supervisingVet.firstName} {contact.supervisingVet.lastName}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    }
                  />
                </ListItem>
                {index < contacts.length - 1 && <Divider />}
              </Fragment>
            ))}
          </List>
        )}
      </Paper>

      {/* Add/Edit Contact Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingContact ? 'Edit Contact' : 'Add Contact'}
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
              <TextField
                label="Prefix"
                value={formData.prefix}
                onChange={(e) => setFormData({ ...formData, prefix: e.target.value })}
                disabled={loading}
                placeholder="Dr., Mr., etc."
              />
              <TextField
                label="First Name *"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                disabled={loading}
                required
              />
              <TextField
                label="Last Name *"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                disabled={loading}
                required
              />
            </Box>
            <TextField
              label="Suffix"
              value={formData.suffix}
              onChange={(e) => setFormData({ ...formData, suffix: e.target.value })}
              disabled={loading}
              placeholder="DVM, PhD, etc."
              fullWidth
            />
            <TextField
              label="Email *"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              disabled={loading}
              required
              fullWidth
            />
            <TextField
              label="Phone"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              disabled={loading}
              fullWidth
            />
            <TextField
              select
              label="Role *"
              value={formData.role}
              onChange={(e) => {
                // Clear supervising vet if role changes away from VET_TECH
                const newRole = e.target.value;
                setFormData({ 
                  ...formData, 
                  role: newRole,
                  supervisingVetContactId: newRole === 'VET_TECH' ? formData.supervisingVetContactId : ''
                });
              }}
              disabled={loading}
              required
              fullWidth
            >
              {CONTACT_ROLES.map((role) => (
                <MenuItem key={role} value={role}>
                  {formatRole(role)}
                </MenuItem>
              ))}
            </TextField>
            {formData.role === 'VET_TECH' && (
              <TextField
                select
                label="Assigned Vet (Optional)"
                value={formData.supervisingVetContactId || ''}
                onChange={(e) => setFormData({ ...formData, supervisingVetContactId: e.target.value })}
                disabled={loading}
                fullWidth
                helperText="Select a vet who supervises this vet tech"
              >
                <MenuItem value="">
                  <em>None (Standalone Vet Tech)</em>
                </MenuItem>
                {contacts
                  .filter(c => c.role === 'VET' && c.id !== editingContact?.id)
                  .map((vet) => (
                    <MenuItem key={vet.id} value={vet.id}>
                      {[vet.prefix, vet.firstName, vet.lastName, vet.suffix].filter(Boolean).join(' ')}
                      {vet.email && ` (${vet.email})`}
                    </MenuItem>
                  ))}
              </TextField>
            )}
            <TextField
              label="Title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              disabled={loading}
              placeholder="Job title or position"
              fullWidth
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <input
                type="checkbox"
                id="isPrimary"
                checked={formData.isPrimary}
                onChange={(e) => setFormData({ ...formData, isPrimary: e.target.checked })}
                disabled={loading}
              />
              <label htmlFor="isPrimary" style={{ fontFamily: 'Poppins, sans-serif', fontSize: '14px' }}>
                Primary contact
              </label>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={loading || !formData.firstName || !formData.lastName || !formData.email}
          >
            {loading ? <CircularProgress size={20} /> : (editingContact ? 'Update Contact' : 'Add Contact')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Contact</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Typography sx={{ fontFamily: 'Poppins, sans-serif' }}>
            Are you sure you want to delete this contact? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleDelete} variant="contained" color="error" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Login Request Dialog */}
      <LeadLoginRequestDialog
        open={loginRequestDialogOpen}
        onClose={() => {
          setLoginRequestDialogOpen(false);
          setContactForLogin(null);
          setError('');
        }}
        lead={lead}
        contact={contactForLogin}
        onSuccess={() => {
          setLoginRequestDialogOpen(false);
          setContactForLogin(null);
          onContactUpdated();
        }}
      />
    </>
  );
};

export default LeadContacts;

